import React, { useState } from "react";
import { useLanguage } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkles, Star, Trophy, Clock } from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { getAyahByKey } from "../services/quranService";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { planDailyLesson, planDueLesson, planHardLesson, updateSrsItem, computeStats, getDueCount, getDailyTarget, setDailyTarget, recordDaily, getRetention7d, getStreak, resetVocabProgress, toggleHard, isHard, getHardCount, loadCoreVocab, getSrsStore, getHardSet, evaluateAchievements, getAchievements, getRankCap, setRankCap, importVocabProgress, recordSession, getWeeklyMinutes, getWeeklyMinutesBars, getRawStats, getCoreDataset, setCoreDataset } from "../services/vocabService";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const TypeAnswer = ({ current, finalize, isEnglish, showFirstHint, disabled = false }) => {
  const [val, setVal] = React.useState("");
  const check = () => {
    const isCorrect = val.trim().toLowerCase() === String(current.m_id).trim().toLowerCase();
    finalize(isCorrect);
    setVal("");
  };
  const first = String(current.m_id || "").trim().charAt(0);
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border p-6 text-center">
        <div className="text-2xl font-uthmani leading-relaxed">{current.a}</div>
        <div className="text-xs opacity-70">{current.t}</div>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="flex-1 rounded-xl border px-3 py-2"
          placeholder={isEnglish ? "Type the meaning..." : "Ketik arti..."}
          disabled={disabled}
        />
        <Button onClick={check} className="rounded-xl" disabled={disabled}>{isEnglish ? "Check" : "Cek"}</Button>
      </div>
      {showFirstHint ? (
        <div className="text-xs text-muted-foreground">
          {isEnglish ? "Hint: starts with " : "Petunjuk: mulai dengan "}“{first}”
        </div>
      ) : null}
    </div>
  );
};

const ExampleVerse = ({ current }) => {
  const [data, setData] = React.useState(null);
  const audioRef = React.useRef(null);
  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!current?.ex) return;
      try {
        const res = await getAyahByKey(current.ex.s, current.ex.v);
        if (mounted) setData(res);
      } catch {}
    };
    run();
    return () => { mounted = false; };
  }, [current?.id]);
  if (!current?.ex || !data) return null;
  const strip = (s) => s?.normalize("NFKD").replace(/[\u064B-\u065F\u0670]/g, "");
  const target = strip(current.a);
  const plain = strip(data.arabic);
  const idx = plain.indexOf(target);
  let content = data.arabic;
  if (idx >= 0) {
    content = (
      <>
        <span>{data.arabic.slice(0, idx)}</span>
        <span className="bg-amber-100 px-1 rounded">{data.arabic.slice(idx, idx + target.length)}</span>
        <span>{data.arabic.slice(idx + target.length)}</span>
      </>
    );
  }
  return (
    <div className="rounded-2xl border p-4 mt-2">
      <div className="text-sm text-muted-foreground mb-2">Contoh ayat (QS {current.ex.s}:{current.ex.v})</div>
      <div className="text-xl font-uthmani leading-relaxed text-right" dir="rtl">{content}</div>
      {data.audio ? (
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => audioRef.current && audioRef.current.play()}>Putar Audio</Button>
          <audio ref={audioRef} src={data.audio} preload="none" />
        </div>
      ) : null}
    </div>
  );
};

const Vocabulary = () => {
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [flip, setFlip] = useState({});
  const [lesson, setLesson] = useState(() => planDailyLesson(getDailyTarget()));
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizMode, setQuizMode] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [mode, setMode] = useState("mcq_meaning"); // mcq_meaning | mcq_recognition | type_meaning
  const stats = computeStats();
  const [dueCount, setDueCount] = useState(getDueCount());
  const [target, setTarget] = useState(getDailyTarget());
  const [rankCap, setRank] = useState(getRankCap());
  const [focusCat, setFocusCat] = useState(null);
  const [hardCount, setHardCount] = useState(getHardCount());
  const [wrongIds, setWrongIds] = useState([]);
  const [showTranslitHint, setShowTranslitHint] = useState(() => (localStorage.getItem("vocab-show-translit") === "1"));
  const [showFirstHint, setShowFirstHint] = useState(() => (localStorage.getItem("vocab-hint-first-letter") === "1"));
  const [shuffle, setShuffle] = useState(() => (localStorage.getItem("vocab-shuffle") === "1"));
  const [summary, setSummary] = useState(null);
  const [startTs, setStartTs] = useState(0);
  const [timerSec, setTimerSec] = useState(12 * 60);
  const [paused, setPaused] = useState(false);
  const [newAch, setNewAch] = useState([]);
  const achievements = getAchievements();
  const fileInputRef = React.useRef(null);
  const datasetInputRef = React.useRef(null);
  const [answered, setAnswered] = useState(false);
  const [lastCorrect, setLastCorrect] = useState(false);
  const [optMeaning, setOptMeaning] = useState([]);
  const [optArabic, setOptArabic] = useState([]);

  const shuffleArr = (arr) => [...arr].sort(() => 0.5 - Math.random());

  const startLesson = () => {
    const base = planDailyLesson(target, focusCat || undefined);
    const next = shuffle ? shuffleArr(base) : base;
    setLesson(next);
    setQuizIdx(0);
    setCorrect(0);
    setQuizMode(true);
    setSummary(null);
    setStartTs(Date.now());
    setTimerSec(12 * 60);
    setAnswered(false);
    setLastCorrect(false);
  };
  const startReview = () => {
    const base = planDueLesson(target, focusCat || undefined);
    const next = shuffle ? shuffleArr(base) : base;
    setLesson(next);
    setQuizIdx(0);
    setCorrect(0);
    setQuizMode(true);
    setSummary(null);
    setStartTs(Date.now());
    setTimerSec(12 * 60);
    setAnswered(false);
    setLastCorrect(false);
  };
  const startHard = () => {
    const base = planHardLesson(target, focusCat || undefined);
    const next = shuffle ? shuffleArr(base) : base;
    setLesson(next);
    setQuizIdx(0);
    setCorrect(0);
    setQuizMode(true);
    setSummary(null);
    setStartTs(Date.now());
    setTimerSec(12 * 60);
    setAnswered(false);
    setLastCorrect(false);
  };
  const startRetryWrong = () => {
    const all = loadCoreVocab();
    const chosen = all.filter((w) => wrongIds.includes(w.id));
    if (chosen.length === 0) {
      toast.info(isEnglish ? "No wrong answers" : "Tidak ada jawaban salah");
      return;
    }
    const next = shuffle ? shuffleArr(chosen) : chosen;
    setLesson(next);
    setQuizIdx(0);
    setCorrect(0);
    setQuizMode(true);
    setWrongIds([]);
    setSummary(null);
    setStartTs(Date.now());
    setAnswered(false);
    setLastCorrect(false);
  };
  React.useEffect(() => {
    const i = setInterval(() => setDueCount(getDueCount()), 3000);
    return () => clearInterval(i);
  }, []);
  React.useEffect(() => {
    const k = "vocab-remind-date";
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(k) !== today && getDueCount() > 0) {
      toast.info(isEnglish ? "You have due words. Try Review Due." : "Ada kata due. Coba Review Due.");
      localStorage.setItem(k, today);
    }
  }, []);
  React.useEffect(() => {
    if (!quizMode) {
      const base = planDailyLesson(target, focusCat || undefined);
      const next = shuffle ? shuffleArr(base) : base;
      setLesson(next);
    }
  }, [focusCat, target, shuffle, quizMode]);
  React.useEffect(() => {
    localStorage.setItem("vocab-show-translit", showTranslitHint ? "1" : "0");
  }, [showTranslitHint]);
  React.useEffect(() => {
    localStorage.setItem("vocab-hint-first-letter", showFirstHint ? "1" : "0");
  }, [showFirstHint]);
  React.useEffect(() => {
    localStorage.setItem("vocab-shuffle", shuffle ? "1" : "0");
  }, [shuffle]);

  React.useEffect(() => {
    const current = lesson[quizIdx];
    if (!current || !Array.isArray(lesson)) {
      setOptMeaning([]);
      setOptArabic([]);
      return;
    }
    const meanings = [...lesson]
      .map((w) => w.m_id)
      .filter((m, i, arr) => arr.indexOf(m) === i)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
    if (!meanings.includes(current.m_id)) meanings[0] = current.m_id;
    setOptMeaning(meanings.sort(() => 0.5 - Math.random()));

    const arabics = [...lesson]
      .map((w) => ({ a: w.a, id: w.id }))
      .filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3);
    if (!arabics.find((o) => o.id === current.id)) arabics[0] = { a: current.a, id: current.id };
    setOptArabic(arabics.sort(() => 0.5 - Math.random()));
  }, [quizIdx, lesson]);

  React.useEffect(() => {
    if (!quizMode) return;
    const t = setInterval(() => {
      setTimerSec((s) => {
        if (paused) return s;
        if (s <= 1) {
          clearInterval(t);
          setQuizMode(false);
          const duration = Math.max(1, Math.round((Date.now() - startTs) / 1000));
          const summ = { correct, total: lesson.length, wrong: wrongIds.length, duration };
          setSummary(summ);
          const unlocked = evaluateAchievements(summ);
          setNewAch(unlocked);
          recordSession(duration);
          if (unlocked.length) {
            const all = getAchievements();
            unlocked.forEach((id) => {
              const a = all.find((x) => x.id === id);
              if (a) toast.success(a.title || id);
            });
          }
          toast.info(isEnglish ? "Time’s up" : "Waktu habis");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [quizMode, startTs, paused]);

  return (
    <div className="min-h-screen pb-24 md:pb-32 bg-gradient-to-b from-background to-background/95">
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b">
        <div className="page-container py-4">
          <PageHeader
            title={isEnglish ? "Quran Vocabulary" : "Kosakata Quran"}
            subtitle={isEnglish ? "Daily 10–15 minutes" : "10–15 menit per hari"}
          />
          {achievements.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {achievements.slice(-5).map((a) => (
                <span key={a.id} className="px-3 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs">{a.title || a.id}</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="page-container space-y-6 md:space-y-8">
        <Card className="islamic-gradient text-white overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-4 md:gap-6">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/20 flex items-center justify-center shadow-xl">
                <Sparkles className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold">
                  {isEnglish ? "Daily Lesson" : "Pelajaran Harian"}
                </h2>
                <p className="text-white/80 text-sm md:text-base">
                  {isEnglish
                    ? "Short, fun, and focused on most frequent words"
                    : "Pendek, seru, dan fokus pada kata paling sering"}
                </p>
              </div>
              <Badge className="bg-white/20 text-white rounded-full px-3 py-1">
                <Clock className="w-4 h-4 mr-1" />
                {isEnglish ? "10–15 min" : "10–15 mnt"}
              </Badge>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                  <Star className="w-6 h-6" />
                </div>
                <h3 className="font-bold mb-1">
                  {isEnglish ? "Top 300 Words" : "Top 300 Kata"}
                </h3>
                <p className="text-white/80 text-sm">
                  {isEnglish ? "Covers ~65% of the Quran" : "Mencakup ~65% Al‑Quran"}
                </p>
              </div>
              <div className="p-5 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                  <Trophy className="w-6 h-6" />
                </div>
                <h3 className="font-bold mb-1">
                  {isEnglish ? "Gamified Quizzes" : "Kuis Bergamifikasi"}
                </h3>
                <p className="text-white/80 text-sm">
                  {isEnglish ? "Active recall + repetition" : "Active recall + repetisi"}
                </p>
              </div>
              <div className="p-5 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="font-bold mb-1">
                  {isEnglish ? "Kid‑Friendly" : "Ramah Anak"}
                </h3>
                <p className="text-white/80 text-sm">
                  {isEnglish ? "Colors & rewards parents love" : "Warna & reward disukai orang tua"}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col md:flex-row items-center gap-3">
              <Button
                onClick={startLesson}
                className="rounded-2xl px-6 py-3 bg-white text-primary hover:bg-white/90 font-bold"
              >
                {isEnglish ? "Start Today’s Lesson" : "Mulai Pelajaran Hari Ini"}
              </Button>
              <span className="text-white/80 text-sm">
                {isEnglish ? "Coming soon" : "Segera hadir"}
              </span>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-2">
                {isEnglish ? "Progress Overview" : "Ringkasan Progres"}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {isEnglish
                  ? "Track mastered words, retention, and streak"
                  : "Lacak kata dikuasai, retensi, dan streak"}
              </p>
              <div className="h-24 rounded-xl bg-muted/50 border border-border/50 flex items-center justify-center text-muted-foreground">
                {isEnglish ? "Charts coming soon" : "Grafik segera hadir"}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-2">
                {isEnglish ? "Categories" : "Kategori"}
              </h3>
              <Separator className="my-2" />
              <div className="flex flex-wrap gap-2">
                {[
                  isEnglish ? "Particles" : "Partikel",
                  isEnglish ? "Pronouns" : "Pronomina",
                  isEnglish ? "Verbs" : "Kata Kerja",
                  isEnglish ? "Nouns" : "Kata Benda",
                  isEnglish ? "Adjectives" : "Kata Sifat",
                  isEnglish ? "Theology" : "Teologi"
                ].map((label) => (
                  <span key={label} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                    {label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-2">
              {isEnglish ? "Today’s Words" : "Kata Hari Ini"}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`px-3 py-1 rounded-full text-xs ${dueCount > 0 ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"}`}>
                {isEnglish ? "Due" : "Due"}: {dueCount}
              </span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">{isEnglish ? "Focus" : "Fokus"}</span>
                {["pronoun","particle","verb","noun","theology","adjective"].map((c) => (
                  <button
                    key={c}
                    className={`text-xs h-7 px-2 rounded ${focusCat === c ? "bg-primary text-primary-foreground" : "border"}`}
                    onClick={() => setFocusCat(focusCat === c ? null : c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">{isEnglish ? "Pack" : "Paket"}</span>
                {[30,60,100,999].map((n) => (
                  <button
                    key={n}
                    className={`text-xs h-7 px-2 rounded ${rankCap === n ? "bg-primary text-primary-foreground" : "border"}`}
                    onClick={() => { setRank(n); setRankCap(n); }}
                  >
                    {n === 999 ? (isEnglish ? "All" : "Semua") : n}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">{isEnglish ? "Target" : "Target"}</span>
                {[6,8,10].map((n) => (
                  <button
                    key={n}
                    className={`text-xs h-7 px-2 rounded ${target === n ? "bg-primary text-primary-foreground" : "border"}`}
                    onClick={() => { setTarget(n); setDailyTarget(n); }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="md:ml-auto flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={startLesson}>{isEnglish ? "Start Lesson" : "Mulai Pelajaran"}</Button>
                <Button size="sm" onClick={startReview}>{isEnglish ? "Review Due" : "Review Due"}</Button>
                <Button size="sm" variant="secondary" onClick={startHard}>{isEnglish ? "Hard Session" : "Latih Sulit"}</Button>
                <span className="text-xs text-muted-foreground">{isEnglish ? "Hard:" : "Sulit:"} {hardCount}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Button size="sm" variant="outline" onClick={() => { const weak = stats.weakCats?.[0] || null; if (weak) { setFocusCat(weak); startLesson(); } else { toast.info(isEnglish ? "No weak category" : "Tidak ada kategori lemah"); } }}>{isEnglish ? "Focus Weak" : "Fokus Lemah"}</Button>
              <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const data = JSON.parse(reader.result);
                      if (importVocabProgress(data)) {
                        toast.success(isEnglish ? "Progress imported" : "Progres diimpor");
                        setDueCount(getDueCount());
                      } else {
                        toast.error(isEnglish ? "Import failed" : "Import gagal");
                      }
                    } catch {
                      toast.error(isEnglish ? "Invalid JSON" : "JSON tidak valid");
                    }
                  };
                  reader.readAsText(file);
                }} />
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>{isEnglish ? "Import JSON" : "Import JSON"}</Button>
              <input ref={datasetInputRef} type="file" accept="application/json" className="hidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const arr = JSON.parse(reader.result);
                      if (Array.isArray(arr) && arr.length) {
                        const ok = setCoreDataset(arr);
                        if (ok) {
                          toast.success(isEnglish ? "Dataset imported" : "Dataset diimpor");
                          const base = planDailyLesson(target, focusCat || undefined);
                          const next = shuffle ? shuffleArr(base) : base;
                          setLesson(next);
                        } else {
                          toast.error(isEnglish ? "Import failed" : "Import gagal");
                        }
                      } else {
                        toast.error(isEnglish ? "Invalid dataset" : "Dataset tidak valid");
                      }
                    } catch {
                      toast.error(isEnglish ? "Invalid JSON" : "JSON tidak valid");
                    }
                  };
                  reader.readAsText(file);
                }} />
              <Button size="sm" variant="outline" onClick={() => datasetInputRef.current?.click()}>{isEnglish ? "Import Dataset" : "Import Dataset"}</Button>
            </div>
            <p className="text-muted-foreground text-sm mb-4">
              {isEnglish ? "Tap card to flip meaning" : "Ketuk kartu untuk melihat arti"}
            </p>
            {!quizMode ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {lesson.map((w, idx) => {
                const k = String(idx);
                const flipped = !!flip[k];
                return (
                  <button
                    key={k}
                    className={`rounded-2xl border px-3 py-4 text-center transition-all duration-300 ${flipped ? "bg-primary text-primary-foreground" : "bg-card"}`}
                    onClick={() => setFlip((p) => ({ ...p, [k]: !p[k] }))}
                  >
                    <div className="text-xl font-uthmani leading-relaxed">{w.a}</div>
                    <div className="text-xs opacity-70">{showTranslitHint ? w.t : ""}</div>
                    <div className="text-sm mt-1 font-semibold">{flipped ? (isEnglish ? w.m_id : w.m_id) : (isEnglish ? "Tap" : "Ketuk")}</div>
                  </button>
                );
              })}
              </div>
            ) : null}
            {quizMode ? (
              <div>
                {(() => {
                  const current = lesson[quizIdx];
                  const finalize = (isCorrect) => {
                    updateSrsItem(current.id, isCorrect ? 5 : 2);
                    recordDaily(isCorrect);
                    if (!isCorrect) setWrongIds((prev) => prev.includes(current.id) ? prev : [...prev, current.id]);
                    setCorrect((c) => c + (isCorrect ? 1 : 0));
                    const next = quizIdx + 1;
                    if (next >= lesson.length) {
                      setQuizMode(false);
                      const duration = Math.max(1, Math.round((Date.now() - startTs) / 1000));
                      const summ = { correct: correct + (isCorrect ? 1 : 0), total: lesson.length, wrong: wrongIds.length + (isCorrect ? 0 : 1), duration };
                      setSummary(summ);
                      const unlocked = evaluateAchievements(summ);
                      setNewAch(unlocked);
                      recordSession(duration);
                      if (unlocked.length) {
                        const all = getAchievements();
                        unlocked.forEach((id) => {
                          const a = all.find((x) => x.id === id);
                          if (a) toast.success(a.title || id);
                        });
                      }
                      toast.success(isEnglish ? `Great! ${correct + (isCorrect ? 1 : 0)} correct` : `Mantap! Benar ${correct + (isCorrect ? 1 : 0)}`);
                    } else {
                      setLastCorrect(isCorrect);
                      setAnswered(true);
                      setTimeout(() => {
                        setAnswered(false);
                        setQuizIdx(next);
                      }, 600);
                    }
                  };
                  return (
                    <div className="space-y-4">
                      {(!current) && (
                        <div className="text-center text-sm text-muted-foreground">{isEnglish ? "Question unavailable" : "Soal tidak tersedia"}</div>
                      )}
                      {(!current) ? null : (
                      <>
                      <div className="text-center text-xs text-muted-foreground">{isEnglish ? "Sisa waktu" : "Sisa waktu"}: {Math.floor(timerSec/60)}:{String(timerSec%60).padStart(2,"0")}</div>
                      <div className="mx-auto w-full max-w-xs h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-2 bg-primary" style={{ width: `${Math.round(((12*60 - timerSec) / (12*60)) * 100)}%` }}></div>
                      </div>
                      <div className="text-center">
                        <Button size="sm" variant="outline" onClick={() => setPaused((p) => !p)}>{paused ? (isEnglish ? "Resume" : "Lanjutkan") : (isEnglish ? "Pause" : "Jeda")}</Button>
                      </div>
                      <div className="mt-2 text-center text-xs text-muted-foreground">
                        {isEnglish ? "Progress" : "Progres"}: {correct}/{lesson.length}
                      </div>
                      <div className="mx-auto w-full max-w-xs h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-2 bg-green-500" style={{ width: `${Math.round(((correct) / Math.max(lesson.length,1)) * 100)}%` }}></div>
                      </div>
                      <div className="rounded-2xl p-4 bg-muted/50 text-foreground">
                        <div className="space-y-1">
                          <div className="text-center text-sm">{isEnglish ? "Mode" : "Mode"}: {mode === "mcq_meaning" ? (isEnglish ? "Meaning MCQ" : "Pilihan Arti") : mode === "mcq_recognition" ? (isEnglish ? "Recognition" : "Pengenalan Arab") : (isEnglish ? "Type Meaning" : "Ketik Arti")}</div>
                        </div>
                      </div>
                      {mode === "mcq_meaning" && (
                        <>
                          <div className="rounded-2xl border p-6 text-center">
                            <div className="text-2xl font-uthmani leading-relaxed">{current.a}</div>
                            <div className="text-xs opacity-70">{showTranslitHint ? current.t : ""}</div>
                            <div className="mt-2">
                              <Button variant={isHard(current.id) ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => { toggleHard(current.id); setHardCount(getHardCount()); toast.success(isEnglish ? (isHard(current.id) ? "Marked hard" : "Unmarked") : (isHard(current.id) ? "Ditandai sulit" : "Dihapus tanda")); }}>
                                <Star className="w-4 h-4 mr-1" />
                                {isEnglish ? "Hard" : "Sulit"}
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {optMeaning.map((opt, i) => (
                              <Button key={i} variant="outline" className="rounded-2xl py-3 bg-primary text-primary-foreground hover:bg-primary/90 border-primary/40 disabled:opacity-50" disabled={answered} onClick={() => finalize(opt === current.m_id)}>
                                {opt}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}
                      {mode === "mcq_recognition" && (
                        <>
                          <div className="rounded-2xl border p-6 text-center">
                            <div className="text-base md:text-lg font-semibold">{isEnglish ? "Select the Arabic for:" : "Pilih Arab dari:"}</div>
                            <div className="mt-1 text-sm text-muted-foreground">{current.m_id}</div>
                            <div className="mt-2">
                              <Button variant={isHard(current.id) ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => { toggleHard(current.id); setHardCount(getHardCount()); toast.success(isEnglish ? (isHard(current.id) ? "Marked hard" : "Unmarked") : (isHard(current.id) ? "Ditandai sulit" : "Dihapus tanda")); }}>
                                <Star className="w-4 h-4 mr-1" />
                                {isEnglish ? "Hard" : "Sulit"}
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {optArabic.map((opt, i) => (
                              <button key={i} className="rounded-2xl border px-3 py-4 text-center bg-primary text-primary-foreground hover:bg-primary/90 border-primary/40 disabled:opacity-50" disabled={answered} onClick={() => finalize(opt.id === current.id)}>
                                <div className="text-xl font-uthmani leading-relaxed">{opt.a}</div>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      {mode === "type_meaning" && (
                        <TypeAnswer current={current} finalize={finalize} isEnglish={isEnglish} showFirstHint={showFirstHint} disabled={answered} />
                      )}
                      <ExampleVerse current={current} />
                      {answered && (
                        <div className="mt-3 rounded-2xl p-4 bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/20 text-foreground">
                          <div className={`text-lg font-bold ${lastCorrect ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>{lastCorrect ? (isEnglish ? "Excellent!" : "Luar biasa!") : (isEnglish ? "Try again!" : "Coba lagi!")}</div>
                        </div>
                      )}
                      </>
                      )}
                      <div className="text-center text-sm text-muted-foreground">
                        {isEnglish ? `Question ${quizIdx + 1} of ${lesson.length}` : `Soal ${quizIdx + 1} dari ${lesson.length}`}
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="ghost" onClick={() => setMode("mcq_meaning")}>{isEnglish ? "Meaning MCQ" : "Pilihan Arti"}</Button>
                        <Button variant="ghost" onClick={() => setMode("mcq_recognition")}>{isEnglish ? "Recognition" : "Pengenalan Arab"}</Button>
                        <Button variant="ghost" onClick={() => setMode("type_meaning")}>{isEnglish ? "Type" : "Ketik Arti"}</Button>
                        <Button variant="outline" onClick={startRetryWrong}>{isEnglish ? "Retry Wrong" : "Ulangi Salah"}</Button>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Button variant={showTranslitHint ? "default" : "outline"} onClick={() => setShowTranslitHint((v) => !v)}>{isEnglish ? "Translit Hint" : "Hint Translit"}</Button>
                        <Button variant={showFirstHint ? "default" : "outline"} onClick={() => setShowFirstHint((v) => !v)}>{isEnglish ? "First Letter" : "Huruf Awal"}</Button>
                        <Button variant={shuffle ? "default" : "outline"} onClick={() => setShuffle((v) => !v)}>{isEnglish ? "Shuffle" : "Acak"}</Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              null
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-2">{isEnglish ? "Your Progress" : "Progres Anda"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border">
                <div className="text-sm text-muted-foreground">{isEnglish ? "Mastered" : "Dikuasai"}</div>
                <div className="text-2xl font-bold">{stats.mastered}/{stats.total}</div>
                <div className="mt-2 h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.round((stats.mastered / Math.max(stats.total,1)) * 100)}%` }}></div>
                </div>
              </div>
              <div className="p-4 rounded-xl border">
                <div className="text-sm text-muted-foreground">{isEnglish ? "Weak Categories" : "Kategori Lemah"}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {stats.weakCats.map((c) => (
                    <span key={c} className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 text-sm">{c}</span>
                  ))}
                </div>
                <div className="mt-3 space-y-2">
                  {Object.entries(stats.catStats).map(([cat, v]) => {
                    const rate = v.total ? Math.round((v.correct / v.total) * 100) : 0;
                    return (
                      <div key={cat}>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{cat}</span>
                          <span>{rate}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                          <div className="h-2 bg-primary" style={{ width: `${rate}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 rounded-xl border">
                <div className="text-sm text-muted-foreground">{isEnglish ? "Streak & Retention" : "Streak & Retensi"}</div>
                <div className="flex items-baseline gap-3 mt-2">
                  <div className="text-2xl font-bold">{getStreak()}<span className="text-sm ml-1">{isEnglish ? "days" : "hari"}</span></div>
                  <div className="flex-1 flex items-end gap-1">
                    {getRetention7d().map((d, i) => (
                      <div key={i} className="w-6 rounded bg-muted overflow-hidden">
                        <div className="bg-primary" style={{ height: `${Math.round(d.rate * 100)}%` }}></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3">
                  <Button variant="outline" size="sm" onClick={() => { resetVocabProgress(); toast.success(isEnglish ? "Progress reset" : "Progres direset"); }}>{isEnglish ? "Reset Progress" : "Reset Progres"}</Button>
                  <Button variant="outline" size="sm" className="ml-2" onClick={() => {
                    const data = { srs: getSrsStore(), hard: [...getHardSet()], stats: getRawStats() };
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "quran-vocab-progress.json";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(isEnglish ? "Exported" : "Diekspor");
                  }}>{isEnglish ? "Export JSON" : "Export JSON"}</Button>
                  <Button variant="outline" size="sm" className="ml-2" onClick={() => {
                    const dataset = getCoreDataset() || loadCoreVocab();
                    const blob = new Blob([JSON.stringify(dataset, null, 2)], { type: "application/json" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "quran-vocab-dataset.json";
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(isEnglish ? "Dataset exported" : "Dataset diekspor");
                  }}>{isEnglish ? "Export Dataset" : "Export Dataset"}</Button>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">{isEnglish ? "This week:" : "Minggu ini:"} {getWeeklyMinutes()} {isEnglish ? "min" : "mnt"}</div>
                <div className="mt-2 flex items-end gap-1">
                  {getWeeklyMinutesBars().map((d, i, arr) => {
                    const max = Math.max(...arr.map(x => x.minutes), 1);
                    const pct = Math.min(100, Math.round((d.minutes / max) * 100));
                    return (
                      <div key={i} className="w-6 rounded bg-muted overflow-hidden">
                        <div className="bg-emerald-500" style={{ height: `${pct}%` }} title={`${d.day.slice(5)}: ${d.minutes}m`}></div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getRetention7d().map((d) => ({ day: d.day.slice(5), rate: Math.round(d.rate * 100) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip formatter={(v) => [`${v}%`, isEnglish ? "Retention" : "Retensi"]} />
                      <Line type="monotone" dataKey="rate" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {summary ? (
          <Card>
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-2">{isEnglish ? "Session Summary" : "Ringkasan Sesi"}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl border">
                  <div className="text-sm text-muted-foreground">{isEnglish ? "Correct" : "Benar"}</div>
                  <div className="text-2xl font-bold">{summary.correct}</div>
                </div>
                <div className="p-4 rounded-xl border">
                  <div className="text-sm text-muted-foreground">{isEnglish ? "Wrong" : "Salah"}</div>
                  <div className="text-2xl font-bold">{summary.wrong}</div>
                </div>
                <div className="p-4 rounded-xl border">
                  <div className="text-sm text-muted-foreground">{isEnglish ? "Total" : "Total"}</div>
                  <div className="text-2xl font-bold">{summary.total}</div>
                </div>
                <div className="p-4 rounded-xl border">
                  <div className="text-sm text-muted-foreground">{isEnglish ? "Duration" : "Durasi"}</div>
                  <div className="text-2xl font-bold">{summary.duration}s</div>
                </div>
              </div>
              <div className="mt-4">
                <h4 className="font-semibold mb-2">{isEnglish ? "Achievements" : "Pencapaian"}</h4>
                <div className="flex flex-wrap gap-2">
                  {(newAch.length ? newAch : getAchievements().map((a) => a.id)).map((id) => (
                    <span key={id} className="px-3 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-sm">{id}</span>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={startRetryWrong}>{isEnglish ? "Retry Wrong" : "Ulangi Salah"}</Button>
                <Button onClick={startReview}>{isEnglish ? "Review Due" : "Review Due"}</Button>
                <Button variant="secondary" onClick={startHard}>{isEnglish ? "Hard Session" : "Latih Sulit"}</Button>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
};

export default Vocabulary;
